import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "axios";
import {
  api,
  backendUrl,
  initApiInstance,
  loadCoreData,
} from "../../common/setupApi";
import { userActions } from "../user/userSlice";
import { jwtDecode } from "jwt-decode";
import { handleApiError } from "../alerts/alertsSlice";
import { selectLang } from "../lang/langSlice";
import { unsubPushNotif } from "../pushNotif/pushNotifSlice";
import { isNotificationSupported } from "../pushNotif/utils";

export const login = (params) => async (dispatch, getState) => {
  dispatch(authActions.loginPending());

  const lang = selectLang(getState());

  const payload = {
    username: params.username.toLowerCase(),
    password: params.password,
  };

  return axios
    .post(`${backendUrl}login/`, payload, {
      headers: {
        "Accept-Language": lang,
        "X-App-Language": lang,
      },
    })
    .then(async (authResult) => {
      dispatch(userActions.getUserPending());

      await initApiInstance(authResult.data.proxy, authResult.data.access);

      return api
        .get("user/")
        .then((userResult) => {
          const { group, staff } = jwtDecode(authResult.data.access);
          dispatch(
            userActions.getUserFulfilled({ ...userResult.data, group, staff })
          );
          dispatch(authActions.loginFulfilled(authResult.data));
          dispatch(loadCoreData({ group, pk: userResult.data.pk }));
        })
        .catch((error) => {
          dispatch(handleApiError(error));
          dispatch(userActions.getUserRejected(error?.response?.data));
          dispatch(authActions.loginRejected(error?.response?.data));
        });
    })
    .catch((error) => {
      dispatch(authActions.loginRejected(error?.response?.data));
      dispatch(handleApiError(error));
    });
};

export const logout = () => async (dispatch) => {
  if (isNotificationSupported && Notification.permission === "granted") {
    dispatch(unsubPushNotif());
  }
  dispatch(authActions.logout());
};

export const refreshUserTokens = createAsyncThunk(
  "auth/refresh",
  async (_, { getState, rejectWithValue }) => {
    const refresh = selectAuth(getState()).token.refresh;

    return api
      .post("token/refresh/", { refresh })
      .then((res) => {
        return {
          access: res.data.access,
          refresh: res.data.refresh,
        };
      })
      .catch((error) => {
        console.error(error?.response);
        // Si le token refresh a expiré
        // l'interceptor ouvrira le re auth, si le user est auth
        return rejectWithValue(error?.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectAuth(getState()).refreshStatus;
      if (status === "pending") {
        return false;
      }
    },
  }
);

const initialState = {
  status: "idle",
  token: {
    access: localStorage.getItem("accessToken") || null,
    refresh: localStorage.getItem("refreshToken") || null,
  },
  proxy: localStorage.getItem("proxy") || null,
  error: null,
  openReAuth: false,
  refreshStatus: "idle",
  isLoadingLogout: false,
  google: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    toggleReAuth: (state, action) => {
      state.openReAuth = action.payload;
    },
    loginPending: (state) => {
      state.status = "pending";
      state.error = null;
    },
    loginFulfilled: (state, action) => {
      localStorage.setItem("accessToken", action.payload.access);
      localStorage.setItem("refreshToken", action.payload.refresh);
      localStorage.setItem("proxy", action.payload.proxy);
      state.token.access = action.payload.access;
      state.token.refresh = action.payload.refresh;
      state.proxy = action.payload.proxy;
      state.status = "fulfilled";
    },
    loginRejected: (state, action) => {
      state.status = "rejected";
      state.error = action.payload;
    },
    setLoadingLogout: (state, action) => {
      state.isLoadingLogout = action.payload;
    },
    logout: (state) => {
      localStorage.setItem("accessToken", "");
      localStorage.setItem("refreshToken", "");
      localStorage.setItem("proxy", "");
      state.status = "idle";
      state.token.access = null;
      state.token.refresh = null;
      state.proxy = null;
      state.error = null;
      state.openReAuth = false;
      state.refreshStatus = "idle";
      api.defaults.baseURL = "";
    },
    setGoogle: (state, action) => {
      state.google = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshUserTokens.pending, (state) => {
        state.refreshStatus = "pending";
      })
      .addCase(refreshUserTokens.fulfilled, (state, action) => {
        localStorage.setItem("accessToken", action.payload.access);
        localStorage.setItem("refreshToken", action.payload.refresh);
        state.token.access = action.payload.access;
        state.token.refresh = action.payload.refresh;
        state.refreshStatus = "fulfilled";
      })
      .addCase(refreshUserTokens.rejected, (state) => {
        state.refreshStatus = "rejected";
      });
  },
});

export const toggleReAuth = authSlice.actions.toggleReAuth;

const _selectAuth = (state) => state.auth;

export const selectAuth = createSelector(
  [(state) => _selectAuth(state)],
  (auth) => {
    const proxy = auth.proxy;
    const token = {
      access: auth.token.access,
      refresh: auth.token.refresh,
      // access: localStorage.getItem("accessToken"),
      // refresh: localStorage.getItem("refreshToken")
    };
    const status = auth.status;
    const error = auth.error;
    const openReAuth = auth.openReAuth;
    const refreshStatus = auth.refreshStatus;
    const isLoadingLogout = auth.isLoadingLogout;
    const isLoading = status === "pending";
    const isAuth =
      token.access !== null &&
      token.access !== undefined &&
      token.access !== "";
    return {
      isAuth,
      status,
      error,
      isLoading,
      proxy,
      token,
      openReAuth,
      refreshStatus,
      isLoadingLogout,
    };
  }
);

export const selectGoogleUser = (state) => state.auth.google;

export const authActions = authSlice.actions;

export default authSlice.reducer;
